import type { BaseFile } from '@/shared/DriveTypes';
import { Check, DoneAll, DoNotDisturb, InfoOutlined, OpenInNew } from '@mui/icons-material';
import { Checkbox, CircularProgress, Fade, Tooltip } from '@mui/material';
import { getContrast, meetsContrastGuidelines } from 'polished';
import { Suspense, useEffect, useState, type ChangeEvent } from 'react';
import { useSWRConfig } from 'swr';
import { Link, useLocation } from 'wouter';
import { cache } from '~/cache';
import * as buttonStyles from '~/elements/Buttons/Button.css';
import Title from '~/elements/Title/Title';
import { DEV } from '~/utils/constants';
import * as DriveAPI from '~/utils/DriveAPI';
import { DrivePicker } from '~/utils/DrivePicker';
import { keys, useAuth, useRootFolder, useWikis } from '~/utils/hooks/hooks';
import { lazyWithPreload } from '~/utils/lazyWithPreload';
import { loadedKeys } from '~/utils/middleware/loadOnce';
import type { Profile } from '~/utils/types';
import { getId, ls } from '~/utils/utils';
import * as formStyles from '../Form.css';
import * as styles from './Settings.css';
import { WikiDomainSelectorLoader } from './WikiDomainSelector';

const ColorInput = lazyWithPreload(() => import('~/elements/ColorInput/ColorInput'), 'ColorInput');
const EmojiInput = lazyWithPreload(() => import('~/elements/EmojiInput/EmojiInput'), 'EmojiInput');

const DEFAULT_SIDEBAR_COLOR = '#638AF5';
const DEFAULT_FONT_COLOR = '#FFFFFF';

const hasAlpha = /rgba[^)]*(?:\.\d*|0)\)|#\w{7,}/;

const tryCatch = <T,>(fn: () => T) => {
  try {
    return fn();
  } catch {
    return null;
  }
};

const ContrastRatio = ({ colors }: { colors: [string, string] }) => {
  if (!colors[0] || !colors[1]) return null;
  if (hasAlpha.test(colors[0]) || hasAlpha.test(colors[1])) return null;

  const contrast = tryCatch(() => getContrast(colors[0], colors[1]).toFixed(2));
  const guidelines = tryCatch(() => meetsContrastGuidelines(colors[0], colors[1]));
  if (!contrast || !guidelines) return null;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
      <div>Contrast ratio: {contrast}</div>
      <Tooltip
        title={
          <div style={{ fontSize: 14, padding: '2px 2px 6px 2px' }}>
            <h4 style={{ margin: '0 0 8px 0' }}>Contrast ratios</h4>
            <div style={{ marginBottom: 6 }}>
              <div style={{ fontWeight: 600, marginBottom: 2 }}>Normal text</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                AA: 4.5{' '}
                {guidelines.AA ? (
                  <Check htmlColor="#5b76f6" style={{ fontSize: 18 }} />
                ) : (
                  <DoNotDisturb htmlColor="#ff4444" style={{ fontSize: 16 }} />
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                AAA: 7.0{' '}
                {guidelines.AAA ? (
                  <Check htmlColor="#5b76f6" style={{ fontSize: 18 }} />
                ) : (
                  <DoNotDisturb htmlColor="#ff4444" style={{ fontSize: 16 }} />
                )}
              </div>
            </div>
            <div>
              <div style={{ fontWeight: 600, marginBottom: 2, marginTop: 2 }}>Large text</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                AA: 3.0{' '}
                {guidelines.AALarge ? (
                  <Check htmlColor="#5b76f6" style={{ fontSize: 18 }} />
                ) : (
                  <DoNotDisturb htmlColor="#ff4444" style={{ fontSize: 16 }} />
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                AAA: 4.5{' '}
                {guidelines.AAALarge ? (
                  <Check htmlColor="#5b76f6" style={{ fontSize: 18 }} />
                ) : (
                  <DoNotDisturb htmlColor="#ff4444" style={{ fontSize: 16 }} />
                )}
              </div>
            </div>
            <a
              href="https://webaim.org/articles/contrast/#ratio"
              target="_blank"
              rel="noreferrer"
              style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: '.5rem', color: '#fff' }}
            >
              Find out more <OpenInNew style={{ fontSize: 16 }} />
            </a>
          </div>
        }
        enterDelay={100}
        placement="right"
        arrow
        slots={{ transition: Fade }}
        slotProps={{
          tooltip: { sx: { backgroundColor: '#1a1a1a' } },
          arrow: { sx: { color: '#1a1a1a' } },
        }}
      >
        <div style={{ cursor: 'help', display: 'flex', alignItems: 'center' }}>
          {guidelines.AAA ? (
            <DoneAll htmlColor="#5b76f6" />
          ) : guidelines.AA ? (
            <Check htmlColor="#5b76f6" />
          ) : (
            <InfoOutlined htmlColor="#7b7b7b" style={{ fontSize: 18 }} />
          )}
        </div>
      </Tooltip>
    </div>
  );
};

const getLogoUrl = (logoId: string) => `https://lh3.googleusercontent.com/d/${logoId}=w${68 * window.devicePixelRatio}`;

type PropertyKey = keyof NonNullable<BaseFile['properties']>;
type InterfaceSetting = {
  label: string;
  name: PropertyKey;
  defaultChecked: boolean;
  ownerOnly?: boolean;
};

const interfaceSettingsConfig: InterfaceSetting[] = [
  { label: 'Animations', name: 'animations', defaultChecked: true },
  { label: 'Page titles', name: 'showPageTitle', defaultChecked: true },
  { label: 'Show "created by/last updated"', name: 'showLastModified', defaultChecked: true },
  { label: '"View" and "View in Drive" button for readers', name: 'showViewButtons', defaultChecked: true },
  { label: 'Create new wiki button for readers', name: 'showCreateWikiButton', defaultChecked: true },
  { label: 'Show user menu on custom domain', name: 'userMenu', defaultChecked: true },
  { label: 'Show border around sheets', name: 'sheetBorder', defaultChecked: true },
  { label: 'Link to menu', name: 'highlightTextMenu', defaultChecked: false },
  { label: 'Collapse sidebar menu by default', name: 'collapsedMenu', defaultChecked: false },
  { label: 'Print button', name: 'showPrintButton', defaultChecked: false },
  { label: 'Disable file icons', name: 'disableFileIcons', defaultChecked: false },
  { label: 'Inline editing', name: 'showInlineEditing', defaultChecked: false },
  { label: 'Show wiki selector', name: 'wikiSelector', defaultChecked: true },
  { label: 'Hide emails in share popover', name: 'hideEmailsInSharePopover', defaultChecked: false, ownerOnly: true },
  { label: 'Server side caching', name: 'cache', defaultChecked: true },
];

export const Settings = ({ wikiId }: { wikiId: string }) => {
  const [, navigate] = useLocation();
  const { data: user } = useAuth();
  const { data: wikis, mutate: mutateWikis } = useWikis();
  const { data: rootFolder } = useRootFolder();
  const { mutate } = useSWRConfig();
  const wiki = wikis?.find((w) => w.id === wikiId);
  const isSharedDrive = user?.wikis?.includes(wikiId);
  const shortcutId = wiki?.shortcutId;

  const [name, setName] = useState<string>();
  const [sidebarColor, setSidebarColor] = useState<string>();
  const [fontColor, setFontColor] = useState<string>();
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showFontColorPicker, setShowFontColorPicker] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [logoUrl, setLogoUrl] = useState('');
  const [domainInput, setDomainInput] = useState('');
  const [domainLoading, setDomainLoading] = useState(false);
  const [domainError, setDomainError] = useState('');

  const isPaid = DEV || user?.paid;
  const hasStripe = DEV || !!user?.stripe;
  const isOwner = wiki?.permissions?.some(
    (p) => 'emailAddress' in p && p.emailAddress === user?.email && (p.role === 'owner' || p.role === 'organizer')
  );

  const visibleSettings = interfaceSettingsConfig.filter((s) => !s.ownerOnly || isOwner);

  useEffect(() => {
    if (!showColorPicker && !showFontColorPicker) return;
    const handleClick = (e: MouseEvent) => {
      if (e.target instanceof HTMLInputElement) return;
      const colorNode = document.querySelector('.chrome-picker');
      if (colorNode && e.target instanceof HTMLElement && colorNode.contains(e.target)) return;
      setShowColorPicker(false);
      setShowFontColorPicker(false);
    };
    setTimeout(() => document.addEventListener('click', handleClick));
    return () => document.removeEventListener('click', handleClick);
  }, [showColorPicker, showFontColorPicker]);

  useEffect(() => {
    if (!showEmojiPicker) return;
    const handleClick = (e: MouseEvent) => {
      const emojiPicker = document.querySelector('em-emoji-picker');
      if (emojiPicker && e.target instanceof HTMLElement && emojiPicker.contains(e.target)) return;
      setShowEmojiPicker(false);
    };
    setTimeout(() => document.addEventListener('click', handleClick));
    return () => document.removeEventListener('click', handleClick);
  }, [showEmojiPicker]);

  const currentName = name ?? wiki?.name ?? '';
  const currentSidebarColor = sidebarColor ?? wiki?.properties?.color ?? DEFAULT_SIDEBAR_COLOR;
  const currentFontColor = fontColor ?? wiki?.properties?.fontColor ?? DEFAULT_FONT_COLOR;
  const logoId = wiki?.properties?.logoId;
  const logoURL = wiki?.properties?.logoURL;
  const emoji = wiki?.properties?.unicode;
  const domainProperty = wiki?.properties?.domain;
  const currentDomain = domainInput || domainProperty || '';

  const getChecked = (setting: InterfaceSetting): boolean => {
    const value = wiki?.properties?.[setting.name];
    if (value === undefined) return setting.defaultChecked;
    return setting.defaultChecked ? value !== 'false' : value === 'true';
  };

  const hasChangedFromDefaults = visibleSettings.some((setting) => {
    const checked = getChecked(setting);
    return checked !== setting.defaultChecked;
  });

  const updateWiki = async (properties: Record<string, string | null>) => {
    if (!wiki || !wikis) return;
    const updatedWiki = { ...wiki, properties: { ...wiki.properties, ...properties } };
    const updatedWikis = wikis.map((w) => (w.id === wikiId ? updatedWiki : w));
    history.replaceState({ revalidate: false }, document.title, window.location.href);
    void mutateWikis(updatedWikis, { revalidate: false });
    void mutate(`${keys.file}${wikiId}`, updatedWiki, { revalidate: false });
    await DriveAPI.update({ id: wikiId, properties: { ...wiki.properties, ...properties } as BaseFile['properties'] });
  };

  const cleanupWiki = async () => {
    loadedKeys.clear();
    ls.remove('current-wiki-id');
    await mutateWikis(wikis?.filter((w) => getId(w) !== wikiId) ?? [], { revalidate: false });
    navigate('/app');
  };

  const handleRemove = async () => {
    if (isSharedDrive) {
      await fetch(`/api/user/wikis/${wikiId}`, { method: 'DELETE' });
      void mutate(
        '/api/user/me',
        (prev: Profile | undefined) => {
          const next = prev ? { ...prev, wikis: (prev.wikis ?? []).filter((id) => id !== wikiId) } : prev;
          return next;
        },
        { revalidate: false }
      );
    } else if (shortcutId) {
      await DriveAPI.trash({ id: shortcutId, useClient: true });
    } else if (rootFolder && wiki?.parents?.includes(rootFolder.id)) {
      await DriveAPI.update({ id: wiki.id, removeParents: rootFolder.id, useClient: true });
    }
    await cleanupWiki();
  };

  const handleTrash = async () => {
    await DriveAPI.trash({ id: wikiId, useClient: true });
    await cleanupWiki();
  };

  const handleCheckboxChange = (e: ChangeEvent<HTMLInputElement>, settingName: string) => {
    void updateWiki({ [settingName]: e.target.checked ? 'true' : 'false' });
  };

  const handleResetToDefaults = async () => {
    const properties: Record<string, string> = {};
    for (const setting of visibleSettings) {
      properties[setting.name] = setting.defaultChecked ? 'true' : 'false';
    }
    await updateWiki(properties);
  };

  const handleUpdateNameAndColors = async () => {
    if (!wiki || !wikis) return;
    const properties: Record<string, string | null> = {};
    if (sidebarColor && sidebarColor !== wiki.properties?.color) {
      properties.color = sidebarColor === DEFAULT_SIDEBAR_COLOR ? null : sidebarColor;
    }
    if (fontColor && fontColor !== wiki.properties?.fontColor) {
      properties.fontColor = fontColor === DEFAULT_FONT_COLOR ? null : fontColor;
    }
    const nameChanged = name && name !== wiki.name;
    const updatedWiki = {
      ...wiki,
      ...(nameChanged && { name }),
      properties: { ...wiki.properties, ...properties },
    };
    const updatedWikis = wikis.map((w) => (w.id === wikiId ? updatedWiki : w));
    history.replaceState({ revalidate: false }, document.title, window.location.href);
    await mutateWikis(updatedWikis, { revalidate: false });
    await mutate(`${keys.file}${wikiId}`, updatedWiki, { revalidate: false });
    await DriveAPI.update({
      id: wikiId,
      ...(nameChanged && { name }),
      ...(Object.keys(properties).length > 0 && {
        properties: { ...wiki.properties, ...properties } as BaseFile['properties'],
      }),
    });
    setName(undefined);
    setSidebarColor(undefined);
    setFontColor(undefined);
  };

  const handleSetEmoji = (native: string) => {
    setShowEmojiPicker(false);
    void updateWiki({ unicode: native, logoId: null });
  };

  const handleRemoveLogo = async () => {
    if (logoId) {
      await DriveAPI.trash({ id: logoId }).catch(() => undefined);
    }
    await updateWiki({ unicode: null, logoId: null, logoURL: null });
  };

  const handleSaveLogoUrl = async () => {
    if (!logoUrl) return;
    await updateWiki({ logoURL: logoUrl, logoId: null, unicode: null });
    setShowUrlInput(false);
    setLogoUrl('');
  };

  const handleSelectImage = async () => {
    if (!wiki) return;
    await DrivePicker.open('logo', {
      wiki,
      onAction: async (data) => {
        if (data.action !== 'picked') return;
        const file = data.docs[0];
        if (!file) return;

        const targetName = 'wiki.logo';
        const movedFile = await DriveAPI.copy({
          id: file.id,
          name: targetName,
          parents: [wiki.id],
        });

        if (movedFile) {
          await updateWiki({ logoId: movedFile.id, unicode: null });
        }
      },
    });
  };

  const handleSetDomain = async () => {
    if (!currentDomain) return;
    setDomainLoading(true);
    setDomainError('');
    const url = currentDomain
      .replace(/^https?:\/\//, '')
      .replace(/\/$/, '')
      .replace(/^www\./, '');
    setDomainInput(url);
    try {
      const res = await fetch('/domain', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: wikiId, url }),
      });
      if (!res.ok) {
        const data = await res.json<{ error?: string }>();
        if (res.status === 409) {
          setDomainError(`This domain is already in use. Please visit ${url} and remove it there first.`);
        } else {
          setDomainError(data.error ?? 'There was an error setting your domain, please contact support.');
        }
        return;
      }
      const data = await res.json<{ url?: string }>();
      if (data.url) {
        await updateWiki({ domain: data.url });
        setDomainInput('');
      }
    } catch {
      setDomainError('There was an error setting your domain, please contact support.');
    } finally {
      setDomainLoading(false);
    }
  };

  const handleRemoveDomain = async () => {
    if (!domainProperty) return;
    setDomainLoading(true);
    try {
      await fetch('/domain', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: wikiId, url: domainProperty }),
      });
      await updateWiki({ domain: null });
      setDomainInput('');
    } finally {
      setDomainLoading(false);
    }
  };

  const hasNameOrColorChanges =
    (name !== undefined && name !== wiki?.name) ||
    (sidebarColor !== undefined && sidebarColor !== (wiki?.properties?.color ?? DEFAULT_SIDEBAR_COLOR)) ||
    (fontColor !== undefined && fontColor !== (wiki?.properties?.fontColor ?? DEFAULT_FONT_COLOR));

  return (
    <div className={formStyles.container}>
      <form className={styles.form} onSubmit={(e) => e.preventDefault()}>
        <br />
        <Link to="/app" className="rel" style={{ zIndex: 1 }}>
          ← Back to wiki
        </Link>
        <Title style={{ marginTop: '1.5rem' }}>Wiki settings</Title>
        <div className={styles.formSection}>
          <label htmlFor="name" className={styles.label}>
            Name
          </label>
          <input
            id="name"
            className={formStyles.input}
            placeholder="E.g. My Wiki"
            value={currentName}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        {isPaid && (
          <>
            <div className={styles.formSection}>
              <label htmlFor="sidebarColor" className={styles.sublabel}>
                Sidebar color
              </label>
              <div style={{ display: 'flex', alignItems: 'center', marginTop: '.7rem', marginLeft: '.25em' }}>
                <button
                  type="button"
                  aria-label="Open sidebar color picker"
                  className={styles.colorBox}
                  style={{ background: currentSidebarColor }}
                  onMouseOver={ColorInput.preload}
                  onClick={() => setShowColorPicker(!showColorPicker)}
                />
                <input
                  id="sidebarColor"
                  className={styles.colorInput}
                  value={currentSidebarColor}
                  onChange={(e) => setSidebarColor(e.target.value)}
                  onMouseOver={ColorInput.preload}
                  onClick={() => setShowColorPicker(true)}
                />
                {currentSidebarColor !== DEFAULT_SIDEBAR_COLOR && (
                  <button
                    type="button"
                    className={styles.linkText}
                    style={{ marginLeft: 12 }}
                    onClick={() => setSidebarColor(DEFAULT_SIDEBAR_COLOR)}
                  >
                    Reset
                  </button>
                )}
              </div>
              {showColorPicker && (
                <div style={{ marginTop: '1rem' }}>
                  <Suspense fallback={<div>Loading...</div>}>
                    <ColorInput
                      color={currentSidebarColor}
                      onChangeComplete={(color) => {
                        const {
                          rgb: { r, g, b, a },
                        } = color;
                        const value = a === 1 ? color.hex.toUpperCase() : `rgba(${r}, ${g}, ${b}, ${a ?? 1})`;
                        setSidebarColor(value);
                      }}
                    />
                  </Suspense>
                </div>
              )}
            </div>

            <div className={styles.formSection}>
              <label htmlFor="fontColor" className={styles.sublabel}>
                Font color
              </label>
              <div style={{ display: 'flex', alignItems: 'center', marginTop: '.7rem', marginLeft: '.25em' }}>
                <button
                  type="button"
                  aria-label="Open font color picker"
                  className={styles.colorBox}
                  style={{ background: currentFontColor }}
                  onMouseOver={ColorInput.preload}
                  onClick={() => setShowFontColorPicker(!showFontColorPicker)}
                />
                <input
                  id="fontColor"
                  className={styles.colorInput}
                  value={currentFontColor}
                  onChange={(e) => setFontColor(e.target.value)}
                  onMouseOver={ColorInput.preload}
                  onClick={() => setShowFontColorPicker(true)}
                />
                {currentFontColor !== DEFAULT_FONT_COLOR && (
                  <button
                    type="button"
                    className={styles.linkText}
                    style={{ marginLeft: 12 }}
                    onClick={() => setFontColor(DEFAULT_FONT_COLOR)}
                  >
                    Reset
                  </button>
                )}
              </div>
              {showFontColorPicker && (
                <div style={{ marginTop: '1rem' }}>
                  <Suspense fallback={<div>Loading...</div>}>
                    <ColorInput
                      color={currentFontColor}
                      onChangeComplete={(color) => {
                        const {
                          rgb: { r, g, b, a },
                        } = color;
                        const value = a === 1 ? color.hex.toUpperCase() : `rgba(${r}, ${g}, ${b}, ${a ?? 1})`;
                        setFontColor(value);
                      }}
                    />
                  </Suspense>
                </div>
              )}
            </div>

            <ContrastRatio colors={[currentSidebarColor, currentFontColor]} />
            <br />

            <div className={styles.buttonRow}>
              <button
                type="button"
                className={buttonStyles.primary}
                disabled={!hasNameOrColorChanges}
                onClick={handleUpdateNameAndColors}
              >
                Update
              </button>
              {hasNameOrColorChanges && (
                <span
                  className={styles.linkText}
                  onClick={() => {
                    setName(undefined);
                    setSidebarColor(undefined);
                    setFontColor(undefined);
                  }}
                >
                  Cancel
                </span>
              )}
            </div>
            <br />

            <hr className={styles.separator} />

            <div className={styles.formSection}>
              <label className={styles.label}>Logo</label>
              <div className={styles.logoRow}>
                {!logoId && !logoURL && !emoji && (
                  <>
                    <button type="button" className={buttonStyles.primary} onClick={handleSelectImage}>
                      Select image
                    </button>
                    <button
                      type="button"
                      className={buttonStyles.primary}
                      onMouseOver={EmojiInput.preload}
                      onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    >
                      Pick Emoji
                    </button>
                    <button
                      type="button"
                      className={buttonStyles.primary}
                      onClick={() => setShowUrlInput(!showUrlInput)}
                    >
                      Custom URL
                    </button>
                  </>
                )}
                {logoId && <img src={getLogoUrl(logoId)} alt="Wiki logo" style={{ height: 34, borderRadius: 3 }} />}
                {logoURL && !logoId && <img src={logoURL} alt="Wiki logo" style={{ height: 34, borderRadius: 3 }} />}
                {emoji && (
                  <button
                    type="button"
                    className={styles.emojiButton}
                    onMouseOver={EmojiInput.preload}
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  >
                    {emoji}
                  </button>
                )}
                {(logoId || logoURL || emoji) && (
                  <span className={styles.linkText} onClick={handleRemoveLogo}>
                    Remove
                  </span>
                )}
              </div>
              {showUrlInput && (
                <div style={{ marginTop: '1rem' }}>
                  <input
                    className={styles.input}
                    type="text"
                    placeholder="E.g. https://example.com/logo.png"
                    value={logoUrl}
                    onChange={(e) => setLogoUrl(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        void handleSaveLogoUrl();
                      }
                    }}
                  />
                  <div className={styles.buttonRow}>
                    <button type="button" className={buttonStyles.primary} onClick={handleSaveLogoUrl}>
                      Save
                    </button>
                    <span className={styles.linkText} onClick={() => setShowUrlInput(false)}>
                      Cancel
                    </span>
                  </div>
                </div>
              )}
              {showEmojiPicker && (
                <Suspense fallback={<div>Loading...</div>}>
                  <EmojiInput onEmojiSelect={(e) => handleSetEmoji(e.native)} theme="light" />
                </Suspense>
              )}
            </div>

            <hr className={styles.separator} />
          </>
        )}

        <div className={styles.formSection}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
            <label className={styles.label} style={{ lineHeight: 1, marginBottom: '1rem' }}>
              Interface
            </label>
            {hasChangedFromDefaults && (
              <span className={styles.linkText} onClick={() => void handleResetToDefaults()}>
                Reset to defaults
              </span>
            )}
          </div>
          {visibleSettings.map((setting) => (
            <div key={setting.name}>
              <div className={styles.checkboxRow}>
                <Checkbox
                  id={setting.name}
                  checked={getChecked(setting)}
                  onChange={(e) => handleCheckboxChange(e, setting.name)}
                  style={{ marginLeft: -10 }}
                />
                <label htmlFor={setting.name} className={styles.checkboxLabel}>
                  {setting.label}
                </label>
              </div>
              {setting.name === 'cache' && (
                <ul className={styles.cacheNotes}>
                  <li>Content is cached securely for 30 days to improve search performance</li>
                </ul>
              )}
            </div>
          ))}
        </div>

        {hasStripe && (
          <>
            <hr className={styles.separator} />
            <div
              className={styles.formSection}
              style={{
                paddingTop: '0.5rem',
                paddingBottom: '1.5rem',
              }}
            >
              <label className={styles.label}>Billing</label>
              <p className={styles.subtitle}>Manage card, plan and billing information. View billing history.</p>
              <button
                type="button"
                className={buttonStyles.primary}
                onClick={() => {
                  window.open('https://billing.stripe.com/p/login/3cs5n86dL3sKeaI000', '_blank');
                }}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: '1rem' }}
              >
                Billing portal <OpenInNew style={{ fontSize: 18 }} />
              </button>
            </div>
          </>
        )}

        {isPaid && (
          <>
            <hr className={styles.separator} />
            <div
              className={styles.formSection}
              style={{
                paddingTop: '0.5rem',
                paddingBottom: '1.5rem',
              }}
            >
              <label className={styles.label}>Custom domain</label>
              <p className={styles.subtitle}>Set a custom domain for your wiki</p>
              <a
                target="_blank"
                rel="noreferrer"
                href="https://docs.youneedawiki.com/app/page/1d9uShSJ8JR0jcsw-xKafM0SU_zHfhf5J_Z01nLmUIbs?p=1NqCSiMuEfPfaHTumR_rX6J8zo7ygjx8q"
                style={{
                  color: '#7b7b7b',
                  marginTop: '1rem',
                  marginBottom: 12,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                Instructions <OpenInNew style={{ fontSize: 16 }} />
              </a>
              {!domainProperty && (
                <div className={styles.inputContainer}>
                  <input
                    className={styles.input}
                    type="text"
                    placeholder="E.g. wiki.mywebsite.com"
                    value={currentDomain}
                    onChange={(e) => {
                      setDomainError('');
                      setDomainInput(e.target.value);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        void handleSetDomain();
                      }
                    }}
                  />
                </div>
              )}
              {domainError && (
                <div
                  style={{
                    marginTop: 14,
                    border: '1px solid #ffbfbb',
                    padding: '1em',
                    background: '#FDECEA',
                    color: '#611a15',
                    lineHeight: 1.5,
                    borderRadius: 4,
                  }}
                >
                  {domainError}
                </div>
              )}
              {!!domainProperty && (
                <a
                  href={`https://${domainProperty}`}
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: '#22275a', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                >
                  https://{domainProperty} <OpenInNew style={{ fontSize: 16 }} />
                </a>
              )}
              <div className={styles.buttonRow} style={{ marginTop: 12 }}>
                {!domainProperty && (
                  <button
                    type="button"
                    className={buttonStyles.primary}
                    disabled={!currentDomain || domainLoading}
                    onClick={handleSetDomain}
                  >
                    Save
                  </button>
                )}
                {!!domainProperty && (
                  <button
                    type="button"
                    className={buttonStyles.danger}
                    disabled={domainLoading}
                    onClick={handleRemoveDomain}
                  >
                    Remove
                  </button>
                )}
                {domainLoading && <CircularProgress size={22} />}
                {!domainProperty && !!currentDomain && !domainLoading && (
                  <span
                    className={styles.linkText}
                    onClick={() => {
                      setDomainInput('');
                      setDomainError('');
                    }}
                  >
                    Cancel
                  </span>
                )}
              </div>
            </div>
          </>
        )}

        <WikiDomainSelectorLoader wikiId={wikiId} />

        <hr className={styles.separator} style={{ marginTop: '2rem' }} />
        <div
          className={styles.formSection}
          style={{
            paddingTop: '0.5rem',
            paddingBottom: '1.5rem',
          }}
        >
          <label className={styles.label}>Clear cache</label>
          <p className={styles.subtitle}>
            Clears the browser cache. May fix some issues with the app if files are not loading correctly.
          </p>
          <button
            type="button"
            className={buttonStyles.primary}
            style={{ marginTop: '1rem' }}
            onClick={() => {
              ls.clear();
              void cache.delete('/wiki').then(() => navigate(`/wiki/${wikiId}`));
            }}
          >
            Clear cache
          </button>
        </div>

        <hr className={styles.separator} />
        <div
          className={styles.formSection}
          style={{
            paddingTop: '0.5rem',
            paddingBottom: '1.5rem',
          }}
        >
          <label className={styles.label}>Remove</label>
          <p className={styles.subtitle}>
            Removes wiki from drop down selector{shortcutId ? ', removes shortcut in YNAW folder,' : ','} does not
            remove Drive folder.
          </p>
          <button type="button" className={buttonStyles.primary} onClick={handleRemove}>
            Remove wiki
          </button>
        </div>

        {DEV && (
          <>
            <hr className={styles.separator} />
            <div
              className={styles.formSection}
              style={{
                paddingTop: '0.5rem',
                paddingBottom: '1.5rem',
              }}
            >
              <label className={styles.label} style={{ color: '#d97706' }}>
                🔧 Dev Settings
              </label>
              <p className={styles.subtitle}>These settings are only visible in development mode.</p>
              <div style={{ marginTop: '1rem' }}>
                <label className={styles.label}>Upgrade</label>
                <p className={styles.subtitle} style={{ marginTop: '0.25rem', marginBottom: '0.5rem' }}>
                  Toggle between free and paid.
                </p>
                <button
                  type="button"
                  className={buttonStyles.primary}
                  onClick={() => {
                    const newPaid = !user?.paid;
                    void mutate('/api/user/me', (u: typeof user) => (u ? { ...u, paid: newPaid } : u), {
                      revalidate: false,
                    });
                    void fetch(`/api/admin/user/${user?.id}`, {
                      method: 'PATCH',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ paid: newPaid }),
                    });
                    navigate('/app');
                  }}
                >
                  Change to {user?.paid ? 'free' : 'paid'}
                </button>
              </div>
              <div style={{ marginTop: '1.5rem' }}>
                <label className={styles.label}>Trash</label>
                <p className={styles.subtitle} style={{ marginTop: '0.25rem', marginBottom: '0.5rem' }}>
                  Moves wiki folder to Google Drive trash.
                </p>
                <button type="button" className={buttonStyles.danger} onClick={handleTrash}>
                  Move to trash
                </button>
              </div>
            </div>
          </>
        )}
      </form>
    </div>
  );
};
